import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { reportsAPI, handleApiError } from '../../services/api';

const ReportModal = ({
  isOpen,
  onClose,
  reportableType,
  reportableId,
  onSuccess,
  post, // Post object with user info
}) => {
  const { t, i18n } = useTranslation();
  const [reasons, setReasons] = useState([]);
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [alreadyReported, setAlreadyReported] = useState(false);

  // Step management
  const [currentStep, setCurrentStep] = useState(1);

  // Additional action states
  const [blockUser, setBlockUser] = useState(false);
  const [hidePost, setHidePost] = useState(false);
  const [muteUser, setMuteUser] = useState(false);

  // Load report reasons and check if already reported
  useEffect(() => {
    if (isOpen && reportableType && reportableId) {
      loadReasons();
      checkIfReported();

      // Also check post.has_reported prop if available
      if (post && post.has_reported) {
        setAlreadyReported(true);
      }
    }
  }, [isOpen, reportableType, reportableId, post]);

  const loadReasons = async () => {
    try {
      setIsLoading(true);

      // Determine type for API
      let type = null;
      if (reportableType) {
        if (reportableType.includes('Post')) type = 'post';
        else if (reportableType.includes('Story')) type = 'story';
        else if (reportableType.includes('User')) type = 'user';
        else if (reportableType.includes('Comment')) type = 'comment';
      }

      const response = await reportsAPI.getReasons(type);
      if (response.data?.status === 'success' && response.data?.data) {
        setReasons(response.data.data);
      }
    } catch (err) {
      console.error('Error loading report reasons:', err);
      setError(t('reportModal.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const checkIfReported = async () => {
    try {
      let response;

      // Use specific routes based on reportable type
      if (reportableType === 'App\\Models\\Post' || reportableType === 'App\Models\Post') {
        response = await reportsAPI.checkPost(reportableId);
      } else if (reportableType === 'App\\Models\\Story' || reportableType === 'App\Models\Story') {
        response = await reportsAPI.checkStory(reportableId);
      } else if (reportableType === 'App\\Models\\User' || reportableType === 'App\Models\User') {
        response = await reportsAPI.checkUser(reportableId);
      } else {
        // Fallback to generic route
        response = await reportsAPI.checkReport(reportableType, reportableId);
      }

      if (response.data?.data?.has_reported) {
        setAlreadyReported(true);
      }
    } catch (err) {
      // Ignore errors for check - user can still try to report
      console.error('Error checking report status:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent submission if already reported
    if (alreadyReported || (post && post.has_reported)) {
      setError(t('reportModal.alreadyReported'));
      return;
    }

    if (!selectedReason) {
      setError(t('reportModal.selectReason'));
      return;
    }

    const selectedReasonData = reasons.find((r) => r.value === selectedReason);
    if (selectedReasonData?.requires_custom_reason && !customReason.trim()) {
      setError(t('reportModal.customReason'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      const customReasonValue = selectedReason === 'other' ? customReason.trim() : null;

      // Submit report with additional actions in payload (for posts only)
      if (reportableType === 'App\\Models\\Post' || reportableType === 'App\Models\Post') {
        await reportsAPI.reportPost(
          reportableId,
          selectedReason,
          customReasonValue,
          blockUser,
          hidePost,
          muteUser
        );
      } else if (reportableType === 'App\\Models\\Story' || reportableType === 'App\\Models\\Story') {
        await reportsAPI.reportStory(
          reportableId,
          selectedReason,
          customReasonValue,
          blockUser,
          muteUser
        );
      } else if (reportableType === 'App\\Models\\User' || reportableType === 'App\Models\User') {
        await reportsAPI.reportUser(reportableId, selectedReason, customReasonValue);
      } else {
        // Fallback to generic route
        const reportData = {
          reportable_type: reportableType,
          reportable_id: reportableId,
          reason: selectedReason,
          custom_reason: customReasonValue,
        };
        await reportsAPI.createReport(reportData);
      }

      setSuccess(true);

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Close modal after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage || t('reportModal.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedReason('');
    setCustomReason('');
    setError('');
    setSuccess(false);
    setAlreadyReported(false);
    setCurrentStep(1);
    setBlockUser(false);
    setHidePost(false);
    setMuteUser(false);
    onClose();
  };

  const handleNext = () => {
    // Prevent proceeding if already reported
    if (alreadyReported || (post && post.has_reported)) {
      setError(t('reportModal.alreadyReported'));
      return;
    }

    if (!selectedReason) {
      setError(t('reportModal.selectReason'));
      return;
    }

    const selectedReasonData = reasons.find((r) => r.value === selectedReason);
    if (selectedReasonData?.requires_custom_reason && !customReason.trim()) {
      setError(t('reportModal.customReason'));
      return;
    }

    setError('');
    setCurrentStep(2);
  };

  const handleBack = () => {
    setError('');
    setCurrentStep(1);
  };


  if (!isOpen) return null;

  const isArabic = i18n.language === 'ar';
  const selectedReasonData = reasons.find((r) => r.value === selectedReason);
  const showCustomReason = selectedReasonData?.requires_custom_reason;

  const modalContent = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-modal-title"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
      }}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ marginBottom: '20px', position: 'relative' }}>
          <h2
            id="report-modal-title"
            style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1F2937',
              margin: '0 0 8px 0',
              paddingRight: '30px',
            }}
          >
            {t('reportModal.title')}
          </h2>
          <p
            style={{
              fontSize: '14px',
              color: '#6B7280',
              margin: 0,
            }}
          >
            {t('reportModal.subtitle')}
          </p>
          {/* Close X Button */}
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#6B7280',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'all 0.2s ease',
              opacity: isSubmitting ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.target.style.color = '#1F2937';
                e.target.style.backgroundColor = '#F3F4F6';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.target.style.color = '#6B7280';
                e.target.style.backgroundColor = 'transparent';
              }
            }}
            aria-label={t('reportModal.cancel')}
          >
            ×
          </button>
        </div>

        {/* Already Reported Message */}
        {alreadyReported && (
          <div
            style={{
              padding: '12px',
              backgroundColor: '#FEF3C7',
              border: '1px solid #FCD34D',
              borderRadius: '8px',
              marginBottom: '20px',
            }}
          >
            <p style={{ margin: 0, color: '#92400E', fontSize: '14px' }}>
              {t('reportModal.alreadyReported')}
            </p>
            <p style={{ margin: '4px 0 0 0', color: '#92400E', fontSize: '12px' }}>
              {t('reportModal.alreadyReportedMessage')}
            </p>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div
            style={{
              padding: '12px',
              backgroundColor: '#D1FAE5',
              border: '1px solid #34D399',
              borderRadius: '8px',
              marginBottom: '20px',
            }}
          >
            <p style={{ margin: 0, color: '#065F46', fontSize: '14px' }}>
              {t('reportModal.success')}
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && !success && (
          <div
            style={{
              padding: '12px',
              backgroundColor: '#FEE2E2',
              border: '1px solid #FCA5A5',
              borderRadius: '8px',
              marginBottom: '20px',
            }}
          >
            <p style={{ margin: 0, color: '#991B1B', fontSize: '14px' }}>
              {error}
            </p>
          </div>
        )}

        {/* Form */}
        {!success && !alreadyReported && (
          <form onSubmit={handleSubmit}>
            {/* Steps Container with Animation */}
            <div style={{ position: 'relative', minHeight: currentStep === 1 ? 'auto' : '300px' }}>
              {/* Step 1: Reason Selection */}
              <div
                style={{
                  position: currentStep === 1 ? 'relative' : 'absolute',
                  top: currentStep === 1 ? 'auto' : 0,
                  left: currentStep === 1 ? 'auto' : 0,
                  width: '100%',
                  opacity: currentStep === 1 ? 1 : 0,
                  transform: currentStep === 1 ? 'translateX(0)' : 'translateX(-30px)',
                  pointerEvents: currentStep === 1 ? 'auto' : 'none',
                  transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  zIndex: currentStep === 1 ? 2 : 1,
                }}
              >
                {/* Reason Selection - Radio Buttons */}
                <div style={{ marginBottom: '20px' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '12px',
                    }}
                  >
                    {t('reportModal.selectReason')}
                  </label>
                  {isLoading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6B7280' }}>
                      {t('reportModal.loading') || 'Loading...'}
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                      }}
                    >
                      {reasons.map((reason) => {
                        const reasonLabel = reason.reasonText;
                        const isSelected = selectedReason === reason.value;

                        return (
                          <label
                            key={reason.value}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '12px',
                              border: `2px solid ${isSelected ? '#EF4444' : '#D1D5DB'}`,
                              borderRadius: '8px',
                              backgroundColor: isSelected ? '#FEF2F2' : '#ffffff',
                              cursor: isSubmitting || isLoading ? 'not-allowed' : 'pointer',
                              transition: 'all 0.2s ease-in-out',
                              opacity: isSubmitting || isLoading ? 0.6 : 1,
                            }}
                            onMouseEnter={(e) => {
                              if (!isSubmitting && !isLoading && !isSelected) {
                                e.currentTarget.style.borderColor = '#EF4444';
                                e.currentTarget.style.backgroundColor = '#FEF2F2';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSubmitting && !isLoading && !isSelected) {
                                e.currentTarget.style.borderColor = '#D1D5DB';
                                e.currentTarget.style.backgroundColor = '#ffffff';
                              }
                            }}
                          >
                            <input
                              type="radio"
                              name="reportReason"
                              value={reason.value}
                              checked={selectedReason === reason.value}
                              onChange={(e) => {
                                setSelectedReason(e.target.value);
                                setCustomReason('');
                                setError('');
                              }}
                              disabled={isSubmitting || isLoading}
                              required
                              style={{
                                marginRight: '12px',
                                width: '18px',
                                height: '18px',
                                cursor: isSubmitting || isLoading ? 'not-allowed' : 'pointer',
                                accentColor: '#EF4444',
                              }}
                            />
                            <span
                              style={{
                                fontSize: '14px',
                                color: '#1F2937',
                                fontWeight: isSelected ? '500' : '400',
                                flex: 1,
                              }}
                            >
                              {reasonLabel}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Custom Reason Input */}
                {showCustomReason && (
                  <div style={{ marginBottom: '20px' }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '8px',
                      }}
                    >
                      {t('reportModal.customReason')}
                    </label>
                    <textarea
                      value={customReason}
                      onChange={(e) => {
                        setCustomReason(e.target.value);
                        setError('');
                      }}
                      disabled={isSubmitting}
                      required
                      placeholder={t('reportModal.customReasonPlaceholder')}
                      rows={4}
                      maxLength={1000}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '14px',
                        color: '#1F2937',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                      }}
                    />
                    <p style={{ fontSize: '12px', color: '#6B7280', margin: '4px 0 0 0' }}>
                      {customReason.length}/1000
                    </p>
                  </div>
                )}

                {/* Step 1 Buttons */}
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end',
                    marginTop: '24px',
                  }}
                >
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    style={{
                      padding: '10px 20px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                      color: '#374151',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      opacity: isSubmitting ? 0.5 : 1,
                    }}
                  >
                    {t('reportModal.cancel')}
                  </button>
                  {post && (reportableType === 'App\\Models\\Post' || reportableType === 'App\\Models\\Post' || reportableType === 'App\\Models\\Story' || reportableType === 'App\\Models\\Story') ? (
                    <button
                      type="button"
                      onClick={handleNext}
                      disabled={isSubmitting || isLoading || !selectedReason}
                      style={{
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '8px',
                        backgroundColor: isSubmitting || isLoading || !selectedReason ? '#9CA3AF' : '#EF4444',
                        color: '#ffffff',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: isSubmitting || isLoading || !selectedReason ? 'not-allowed' : 'pointer',
                        opacity: isSubmitting || isLoading || !selectedReason ? 0.6 : 1,
                      }}
                    >
                      {t('reportModal.next') || 'Next'}
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={isSubmitting || isLoading || !selectedReason}
                      style={{
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '8px',
                        backgroundColor: isSubmitting || isLoading || !selectedReason ? '#9CA3AF' : '#EF4444',
                        color: '#ffffff',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: isSubmitting || isLoading || !selectedReason ? 'not-allowed' : 'pointer',
                        opacity: isSubmitting || isLoading || !selectedReason ? 0.6 : 1,
                      }}
                    >
                      {isSubmitting ? t('reportModal.submitting') : t('reportModal.submit')}
                    </button>
                  )}
                </div>
              </div>

              {/* Step 2: Additional Actions (for posts and stories) */}
              {post && (reportableType === 'App\\Models\\Post' || reportableType === 'App\\Models\\Post' || reportableType === 'App\\Models\\Story' || reportableType === 'App\\Models\\Story') && (
                <div
                  style={{
                    position: currentStep === 2 ? 'relative' : 'absolute',
                    top: currentStep === 2 ? 'auto' : 0,
                    left: currentStep === 2 ? 'auto' : 0,
                    width: '100%',
                    opacity: currentStep === 2 ? 1 : 0,
                    transform: currentStep === 2 ? 'translateX(0)' : 'translateX(30px)',
                    pointerEvents: currentStep === 2 ? 'auto' : 'none',
                    transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    zIndex: currentStep === 2 ? 2 : 1,
                  }}
                >
                  <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '12px',
                      }}
                    >
                      {t('reportModal.additionalActions')}
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {/* Block User */}
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          opacity: isSubmitting ? 0.6 : 1,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={blockUser}
                          onChange={(e) => setBlockUser(e.target.checked)}
                          disabled={isSubmitting}
                          style={{
                            marginRight: '12px',
                            marginTop: '2px',
                            width: '18px',
                            height: '18px',
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                            accentColor: '#EF4444',
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', fontWeight: '500', color: '#1F2937', marginBottom: '2px' }}>
                            {t('reportModal.blockUser')}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6B7280' }}>
                            {t('reportModal.blockUserDescription')}
                          </div>
                        </div>
                      </label>

                      {/* Hide Post - Only show for posts, not stories */}
                      {(reportableType === 'App\\Models\\Post' || reportableType === 'App\\Models\\Post') && (
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                            opacity: isSubmitting ? 0.6 : 1,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={hidePost}
                            onChange={(e) => setHidePost(e.target.checked)}
                            disabled={isSubmitting}
                            style={{
                              marginRight: '12px',
                              marginTop: '2px',
                              width: '18px',
                              height: '18px',
                              cursor: isSubmitting ? 'not-allowed' : 'pointer',
                              accentColor: '#EF4444',
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#1F2937', marginBottom: '2px' }}>
                              {t('reportModal.hidePost')}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6B7280' }}>
                              {t('reportModal.hidePostDescription')}
                            </div>
                          </div>
                        </label>
                      )}

                      {/* Mute User */}
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          opacity: isSubmitting ? 0.6 : 1,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={muteUser}
                          onChange={(e) => setMuteUser(e.target.checked)}
                          disabled={isSubmitting}
                          style={{
                            marginRight: '12px',
                            marginTop: '2px',
                            width: '18px',
                            height: '18px',
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                            accentColor: '#EF4444',
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', fontWeight: '500', color: '#1F2937', marginBottom: '2px' }}>
                            {t('reportModal.muteUser')}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6B7280' }}>
                            {t('reportModal.muteUserDescription')}
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Step 2 Buttons */}
                  <div
                    style={{
                      display: 'flex',
                      gap: '12px',
                      justifyContent: 'flex-end',
                      marginTop: '24px',
                    }}
                  >
                    <button
                      type="button"
                      onClick={handleBack}
                      disabled={isSubmitting}
                      style={{
                        padding: '10px 20px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        color: '#374151',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        opacity: isSubmitting ? 0.5 : 1,
                      }}
                    >
                      {t('reportModal.back') || 'Back'}
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      style={{
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '8px',
                        backgroundColor: isSubmitting ? '#9CA3AF' : '#EF4444',
                        color: '#ffffff',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        opacity: isSubmitting ? 0.6 : 1,
                      }}
                    >
                      {isSubmitting ? t('reportModal.submitting') : t('reportModal.submit')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </form>
        )}

        {/* Close button when already reported or success */}
        {(alreadyReported || success) && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '20px',
            }}
          >
            <button
              onClick={handleClose}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: '#EF4444',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              {t('reportModal.cancel')}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ReportModal;

